import { TableSkeleton } from "components/PortalLoading";
import { useState } from "react";
import { Sidebar } from "primereact/sidebar";
import { DataTable } from "components/PortalDataTable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { BsArrowRight } from "react-icons/bs";
import useFetchPublisherBooks from "api/BookResource/useFetchPublisherBooks";
import useBooksFilterStore from "store/booksFilterStore";
import BookFilter from "admin/AllBooks2/BookFilter";
import { CustomPaginator } from "components";
import BookCoverPreview from "components/BookCoverPreview";
import { activeCurrencies } from "utils";
import { Card } from "primereact/card";

const PublisherBooks = ({ userId }) => {
	const [openBooks, setOpenBooks] = useState(false);
	const [expandedRows, setExpandedRows] = useState(null);

	const [firstPage, setFirstPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);

	const { bookFilters, page, setPage } = useBooksFilterStore();

	const { data: books, isLoading: isLoadingBooks } = useFetchPublisherBooks(
		{
			page: page,
			rows_per_page: rowsPerPage,
			...bookFilters,
		},
		userId
	);

	const booksList = books?.data?.records || [];
	const totalRecords = books?.data?.total_records || 0;

	// Column Templates
	const bookCoverTemplate = (rowData) => {
		return (
			<BookCoverPreview
				coverUrl={rowData?.book_cover?.url}
				title={rowData?.title}
				thumbnailWidth={56}
				thumbnailHeight={70}
			/>
		);
	};

	const titleTemplate = (rowData) => {
		return (
			<div className="flex flex-col">
				<span className="font-semibold text-gray-900 text-sm">{rowData?.title}</span>
				{rowData?.isbn && (
					<span className="text-xs text-gray-400">ISBN: {rowData?.isbn}</span>
				)}
			</div>
		);
	};

	const authorTemplate = (rowData) => {
		return (
			<span className="text-sm text-gray-700">{rowData?.author}</span>
		);
	};

	const categoryTemplate = (rowData) => {
		return (
			<span className="text-xs text-gray-600">
				{rowData?.category?.name || "N/A"}
			</span>
		);
	};

	const priceTemplate = (rowData) => {
		if (rowData?.isFree) {
			return <Tag value="Free" severity="success" className="text-xs" />;
		}
		return (
			<div className="flex flex-col gap-0.5">
				<span className="text-xs">${rowData?.price || 0}</span>
			</div>
		);
	};

	const statusTemplate = (rowData) => {
		return (
			<div className="flex flex-col gap-1">
				<Tag
					value={rowData?.is_active ? "Active" : "Inactive"}
					severity={rowData?.is_active ? "success" : "warning"}
					className="text-xs"
				/>
			</div>
		);
	};

	// Row expansion template
	const rowExpansionTemplate = (data) => {
		return (
			<div className="p-4 bg-gray-50 border-t border-gray-200">
				<Card className="shadow-none border-0">
					<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
						{/* Book Details */}
						<div>
							<h4 className="font-semibold text-lg mb-3 text-gray-800">Book Details</h4>
							<div className="space-y-2 text-sm">
								<div>
									<span className="font-medium text-gray-500">ISBN:</span>{" "}
									<span className="text-gray-800">{data?.isbn || "N/A"}</span>
								</div>
								<div>
									<span className="font-medium text-gray-500">Category:</span>{" "}
									<span className="text-gray-800">{data?.category?.name || "N/A"}</span>
								</div>
								<div>
									<span className="font-medium text-gray-500">Genre:</span>{" "}
									<span className="text-gray-800">{data?.genres?.[0]?.name || "N/A"}</span>
								</div>
								<div className="flex items-center gap-2">
									<span className="font-medium text-gray-500">Status:</span>
									<Tag
										value={data?.is_active ? "Active" : "Inactive"}
										severity={data?.is_active ? "success" : "danger"}
									/>
								</div>
							</div>
						</div>

						{/* Pricing */}
						<div>
							<h4 className="font-semibold text-lg mb-3 text-gray-800">Pricing</h4>
							<div className="space-y-2 text-sm">
								<div>
									<span className="font-medium text-gray-500">Price ({activeCurrencies.USD.label}):</span>{" "}
									<span className="text-gray-800">${data?.price || 0}</span>
								</div>
								<div>
									<span className="font-medium text-gray-500">Price ({activeCurrencies.ZWL.label}):</span>{" "}
									<span className="text-gray-800">${data?.zwl_price || 0}</span>
								</div>
								<div className="flex items-center gap-2">
									<span className="font-medium text-gray-500">Free:</span>
									<Tag
										value={data?.isFree ? "Yes" : "No"}
										severity={data?.isFree ? "success" : "secondary"}
									/>
								</div>
								<div className="flex items-center gap-2">
									<span className="font-medium text-gray-500">Sellable:</span>
									<Tag
										value={data?.sellable ? "Yes" : "No"}
										severity={data?.sellable ? "success" : "secondary"}
									/>
								</div>
								<div className="flex items-center gap-2">
									<span className="font-medium text-gray-500">On Subscription:</span>
									<Tag
										value={data?.subscription ? "Yes" : "No"}
										severity={data?.subscription ? "success" : "secondary"}
									/>
								</div>
							</div>
						</div>
					</div>

					{/* Description */}
					{data?.description && (
						<div className="mt-6 pt-4 border-t border-gray-200">
							<h4 className="font-semibold text-lg mb-3 text-gray-800">Description</h4>
							<div
								className="text-sm text-gray-700 prose prose-sm max-w-none"
								dangerouslySetInnerHTML={{
									__html: data?.description || "No description available",
								}}
							/>
						</div>
					)}
				</Card>
			</div>
		);
	};

	return (
		<>
			<button
				type="button"
				onClick={() => setOpenBooks(true)}
				className="text-blue-900 font-bold flex items-center gap-2 hover:text-blue-700 transition-colors"
			>
				View books <BsArrowRight size={20} />
			</button>

			<Sidebar
				visible={openBooks}
				icons={
					<h4 className="font-cera-black text-akgreenest text-2xl">
						Uploaded books
					</h4>
				}
				blockScroll
				onHide={() => setOpenBooks(false)}
				className="font-sans"
				fullScreen
			>
				<hr className="mb-4" />

				<BookFilter hideFilters={["publisher"]} />

				{isLoadingBooks ? (
					<TableSkeleton label="Loading publisher books…" columns={[{ label: "Book", kind: "book" }, "Price", "Status"]} />
				) : (
					<div className="flex flex-col items-center pt-6">
						{/* Stats */}
						<div className="mb-4 text-center">
							<span className="text-lg font-semibold text-gray-700">
								Total: <span className="text-akgreener">{totalRecords}</span> books
							</span>
						</div>

						{/* DataTable */}
						<div className="w-full px-4">
							<DataTable
								value={booksList}
								expandedRows={expandedRows}
								onRowToggle={(e) => setExpandedRows(e.data)}
								rowExpansionTemplate={rowExpansionTemplate}
								dataKey="id"
								tableClassName="font-sans text-sm"
								loading={isLoadingBooks}
								emptyMessage="No books found"
							>
								<Column
									expander
									style={{ width: "3rem" }}
								/>
								<Column
									header="Cover"
									body={bookCoverTemplate}
									style={{ width: "70px" }}
									className="text-center"
								/>
								<Column
									header="Title"
									body={titleTemplate}
									style={{ minWidth: "200px" }}
								/>
								<Column
									header="Author"
									body={authorTemplate}
									style={{ minWidth: "150px" }}
								/>
								<Column
									header="Category"
									body={categoryTemplate}
									style={{ width: "120px" }}
								/>
								<Column
									header="Price"
									body={priceTemplate}
									style={{ width: "80px" }}
								/>
								<Column
									header="Status"
									body={statusTemplate}
									style={{ width: "90px" }}
									className="text-center"
								/>
							</DataTable>
						</div>

						{/* Pagination */}
						<div className="flex justify-center mt-6">
							<CustomPaginator
								firstPage={firstPage}
								rowsPerPage={rowsPerPage}
								setFirstPage={setFirstPage}
								setRowsPerPage={setRowsPerPage}
								totalRecords={totalRecords}
								setPage={setPage}
							/>
						</div>
					</div>
				)}
			</Sidebar>
		</>
	);
};

export default PublisherBooks;
